import { useEffect, useRef, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown, Leaf, Plug, ShieldCheck } from 'lucide-react';
import type { TelemetryPassportClientResponse, PassportFingerprint, PassportWarranty } from '../dto/telemetry-passport.js';
import { PASSPORT_ENVIRONMENTAL_PENDING_TEXT, PASSPORT_WARRANTY_DISCLOSURE_TEXT } from '../dto/telemetry-passport.js';
import { DIRECTIONAL_BASELINE_LABEL } from '../directional-baseline.js';
import {
  usePhaseNotComputedLine,
  WARRANTY_SECTION_HEADING,
  warrantyLeadLine,
  warrantySummaryLines,
} from '../passport/display.js';
import { SiliconXMark } from '../passport/logo.js';
import { CARBON_EMBODIED_EXPLAINER } from '../dto/carbon.js';
import type { ConfidenceTier } from '../scoring/confidence-tier.js';
import { confidenceTierLabel } from '../scoring/confidence-tier.js';
import { horizonBandInfo } from '../scoring/horizon-band.js';
import {
  confidenceDisclosureLine,
  fitnessLine,
  damageBudgetHeadroomPct,
  formatDamageBudgetCaption,
  disclosureLabel,
  disclosureExplanation,
  DISCLOSURES_CHECKED,
  maskSerial,
  lifecycleMilestones,
  provenanceEventDescription,
  SERIAL_MASK_NOTE,
  SERIAL_WITHHELD_TEXT,
  USER_PROVIDED_SERIAL_LABEL,
  USER_PROVIDED_SERIAL_NOTE,
} from '../passport/display.js';
import { HealthScoreCategoryChip } from './TelemetryScoreCallout.js';
import { HorizonBandChip } from './HorizonBandChip.js';
import { UsdCell } from './UsdCell.js';
import { formatDate } from './format.js';
import { VerificationOutcome } from './VerificationOutcome.js';

/**
 * One collapsible passport section. Native `<details>` — the open/closed state
 * lives in the DOM, so there is no React state to manage, and keyboard focus,
 * Enter/Space toggling, and screen-reader expanded/collapsed announcements come
 * from the element itself rather than from ARIA we would have to maintain.
 *
 * `defaultOpen` maps to the plain `open` attribute and is deliberately NOT
 * controlled: React sets the initial value and the browser owns it afterwards,
 * so a re-render never yanks a panel shut under the reader.
 *
 * NOT `DisclosurePanel` (the page-grammar primitive next door), deliberately:
 * that one unmounts its children while closed, so a collapsed section would
 * print as an empty heading and no CSS could rescue it — DOM that isn't there
 * cannot be revealed. A passport that silently drops sections from a printout
 * is exactly the failure this document exists to prevent. `<details>` keeps
 * every section in the DOM and merely hides it.
 *
 * PRINTING. A collapsed panel must never print as an empty heading — this
 * document is filed and read on paper, and silently dropping sections from a
 * printout is the worst failure this component could have.
 *
 * That is done by forcing every panel open on `beforeprint` and restoring the
 * reader's state on `afterprint`, NOT with print CSS. The obvious-looking
 * `@media print { display: block }` on the body does nothing: browsers do not
 * hide collapsed `<details>` content with `display:none` on the child, they
 * hide it at the element's own content slot (Chrome exposes this as
 * `::details-content` with `content-visibility: hidden`), which an author rule
 * on a descendant cannot override. This shipped once as `print:block` and
 * silently printed collapsed sections anyway.
 *
 * The generated PDF artifact does not come through here at all — that is
 * PassportDocument.tsx, which has no collapsing to begin with.
 */
function Panel({
  title,
  defaultOpen = false,
  className = 'bg-card',
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Remembered so a reader who prints does not silently lose the panel state
    // they had arranged on screen.
    let openBeforePrint = el.open;
    const expand = () => {
      openBeforePrint = el.open;
      el.open = true;
      void el.offsetHeight; // force reflow before print paginates
    };
    const restore = () => {
      el.open = openBeforePrint;
    };
    window.addEventListener('beforeprint', expand);
    window.addEventListener('afterprint', restore);
    // Safari historically fires no beforeprint/afterprint and drives printing
    // through this media query listener instead; both paths are harmless when
    // the other also fires, because expand()/restore() are idempotent.
    const mql = window.matchMedia?.('print');
    const onMedia = (e: MediaQueryListEvent) => (e.matches ? expand() : restore());
    mql?.addEventListener?.('change', onMedia);
    return () => {
      window.removeEventListener('beforeprint', expand);
      window.removeEventListener('afterprint', restore);
      mql?.removeEventListener?.('change', onMedia);
    };
  }, []);

  return (
    <details
      ref={ref}
      open={defaultOpen}
      className={`group rounded-lg border border-border ${className}`}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-6 pb-4 [&::-webkit-details-marker]:hidden">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <SiliconXMark size={13} />
          {title}
        </h2>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 print:hidden" />
      </summary>
      <div className="px-6 pb-6">{children}</div>
    </details>
  );
}

/**
 * The presentational §1-§5 digital-passport stack (RUF-555/556), extracted
 * from apps/customer so the ops portal can render the same canonical document
 * (asset-detail-visualizations design). The passport renders identically on
 * every surface — customer standalone page, listing detail embed, lot-member
 * page, and the ops /assets/:assetId/passport view. Confidence renders BEFORE
 * the score: the deliberate anti-laundering ordering from the original page.
 *
 * Every section below §1 is a collapsible `<Panel>`, so the stack collapses
 * identically on all four surfaces — one component, one behavior. The issued
 * PDF is a different component entirely (PassportDocument.tsx) and is always
 * flat; Ctrl+P / "Save as PDF" of THIS component expands every panel first
 * (see Panel's print handling) so a printout never omits a section.
 *
 * Purely presentational — never fetches. Artifact generation/download stays
 * portal-specific (customer composes its IssuedArtifactsCard around this).
 */
export function PassportBody({ passport }: { passport: TelemetryPassportClientResponse }) {
  return (
    <>
      {/* §1 — confidence renders BEFORE the score (deliberate anti-laundering
          ordering), and is the one section that is NOT collapsible: the
          confidence tier is the frame every other number on this document must
          be read through, so it must not be closable ahead of them. */}
      <ConfidenceGauge passport={passport} />
      <Panel title="§2 Health & operational fitness" defaultOpen>
        <HealthSection health={passport.health} />
      </Panel>
      <Panel title="§3 Reliability dossier">
        <ReliabilityDossier damageBudget={passport.damageBudget} />
      </Panel>
      <Panel title="§4 Operational disclosures">
        <OperationalDisclosures disclosures={passport.disclosures} />
      </Panel>
      {/* Active Verification (SIL-585) — synthesized outcome, never a raw
          run dump; same synthesized form the listing embeds via this exact
          component (AssetPassport.tsx wraps PassportBody). */}
      <Panel title="Active verification">
        <ActiveVerificationSection verification={passport.verification} />
      </Panel>
      {/* Provenance chain (SIL-821) — the hash-chained ownership/identity
          history, rendered in order. */}
      <Panel title="Provenance &amp; lifecycle">
        <ProvenanceChainSection provenance={passport.provenance} verification={passport.verification} />
        <LifecycleSection passport={passport} />
      </Panel>
      {/* Manufacturer Entitlement Record (SIL-1919) — owner-only in v1: the API
          sends `warranty: null` to any non-owner viewer, so this panel simply
          never renders for them. Same wording and disclosure as the PDF. */}
      {passport.warranty && (
        <Panel title={WARRANTY_SECTION_HEADING}>
          <WarrantyBlock warranty={passport.warranty} />
        </Panel>
      )}
      {/* Component manifest (SIL-825) — only rendered for an asset whose
          hardware identity is a chassis (775); showManifest is decided by
          the assembler from device_kind, never inferred here from the
          manifest's own content (811's migration header). */}
      {passport.manifest.showManifest && (
        <Panel title="Component manifest">
          <ManifestSection manifest={passport.manifest} />
        </Panel>
      )}
      {/* Environmental — embodied carbon (SIL-190) + operational Scope 2 */}
      <Panel title={ENVIRONMENTAL_SECTION_TITLE}>
        <EnvironmentalBody environmental={passport.environmental} />
      </Panel>
      <Panel title="§5 Disclaimer & verification" className="bg-muted print:bg-white">
        <DisclaimerSection passport={passport} />
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------------------
// Manufacturer Entitlement Record (SIL-1919). Dates as the manufacturer reported
// them, attributed to the source. No status verdict, badge or colour.
// ---------------------------------------------------------------------------

function WarrantyBlock({ warranty }: { warranty: PassportWarranty }) {
  const owner = warranty.projection === 'owner';
  return (
    <div className="space-y-2 text-sm text-foreground">
      <p className="text-muted-foreground">{warrantyLeadLine(warranty)}</p>
      {owner && warranty.scope === 'asset' && (warranty.shipDate || warranty.entitlements.length > 0) ? (
        <table className="w-full text-left text-sm">
          <tbody>
            {warranty.shipDate ? (
              <tr>
                <th className="py-1 pr-3 font-medium text-muted-foreground">Ship date</th>
                <td className="py-1">{warranty.shipDate}</td>
              </tr>
            ) : null}
            {warranty.entitlements.map((e, i) => (
              <tr key={`${e.startDate ?? ''}-${e.endDate ?? ''}-${i}`}>
                <th className="py-1 pr-3 font-medium text-muted-foreground">
                  {e.startDate ?? '—'} to {e.endDate ?? '—'}
                </th>
                <td className="py-1">{e.serviceLevel ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {warrantySummaryLines(warranty).map((line) => (
        <p key={line}>{line}</p>
      ))}
      <p className="text-xs text-muted-foreground">{PASSPORT_WARRANTY_DISCLOSURE_TEXT}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §1 Identity & Coverage
// ---------------------------------------------------------------------------

const CONFIDENCE_TIER_ACCENT: Record<ConfidenceTier, string> = {
  full: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  partial: 'border-amber-300 bg-amber-50 text-amber-800',
  minimal: 'border-red-300 bg-red-50 text-red-800',
};

/**
 * SIL-819 — the Fingerprint's own renderer. Four states, never collapsed
 * into one "unavailable" line: `not_established`, `disputed`, and
 * `suppressed` each get their own distinct copy, and `established` is the
 * ONLY branch that renders a value — and it ALWAYS renders its verification
 * tier alongside it, never hidden/truncated/hover-only. A Tier 3 identity is
 * backed only by a seller-reported, spoofable, in-band serial; hiding that
 * provenance would present it as if it were OOB-attested.
 */
function FingerprintValue({ fingerprint }: { fingerprint: PassportFingerprint }) {
  if (fingerprint.status === 'established') {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <code className="font-mono text-sm text-foreground">{fingerprint.value}</code>
        <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
          Tier {fingerprint.tier} identity
        </span>
      </span>
    );
  }

  if (fingerprint.status === 'not_established') {
    return <span className="text-muted-foreground">Identity not established</span>;
  }

  if (fingerprint.status === 'disputed') {
    return <span className="text-red-700">Identity disputed</span>;
  }

  return <span className="text-amber-700">Identity under review</span>;
}

function ConfidenceGauge({ passport }: { passport: TelemetryPassportClientResponse }) {
  const tier = passport.coverage.confidenceTier;
  const disclosure = tier != null ? confidenceDisclosureLine(tier) : null;
  const coveragePct =
    passport.health != null ? Math.round(passport.health.overallCoverage * 100) : null;

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <SiliconXMark size={13} />
        Identity &amp; coverage
      </h2>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Serial</dt>
          <dd className="mt-1 text-sm text-foreground">
            {passport.identity.serialNumber
              ? maskSerial(passport.identity.serialNumber)
              : SERIAL_WITHHELD_TEXT}
          </dd>
          {passport.identity.serialNumber && (
            <p className="mt-1 text-xs text-muted-foreground">{SERIAL_MASK_NOTE}</p>
          )}
        </div>
        {passport.identity.userProvidedSerial && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {USER_PROVIDED_SERIAL_LABEL}
            </dt>
            <dd className="mt-1 text-sm text-foreground">{maskSerial(passport.identity.userProvidedSerial)}</dd>
            <p className="mt-1 text-xs text-muted-foreground">{USER_PROVIDED_SERIAL_NOTE}</p>
          </div>
        )}
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">SKU</dt>
          <dd className="mt-1 text-sm text-foreground">
            {passport.identity.manufacturerName && passport.identity.skuModelName
              ? `${passport.identity.manufacturerName} ${passport.identity.skuModelName}`
              : (passport.identity.skuModelName ?? '—')}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Fingerprint</dt>
          <dd className="mt-1 text-sm text-foreground">
            <FingerprintValue fingerprint={passport.identity.fingerprint} />
          </dd>
        </div>
      </dl>

      <div className="mt-5 border-t border-border pt-5">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Telemetry confidence
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {tier != null ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${CONFIDENCE_TIER_ACCENT[tier]}`}
            >
              {tier === 'full' ? (
                <ShieldCheck className="size-4" />
              ) : (
                <AlertTriangle className="size-4" />
              )}
              {confidenceTierLabel(tier)}
            </span>
          ) : coveragePct != null ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1 text-sm text-muted-foreground">
              <AlertTriangle className="size-4" />
              Insufficient signal for a confidence tier
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border border-dashed border-border px-3 py-1 text-sm text-muted-foreground">
              Not yet scored
            </span>
          )}
        </div>
        {tier == null && coveragePct != null && (
          <p className="mt-2 text-sm text-muted-foreground">
            Insufficient signal for a confidence tier — based on {coveragePct}% of expected
            signals.
          </p>
        )}
        {disclosure && <p className="mt-2 text-sm text-muted-foreground">{disclosure}</p>}
      </div>

      <CoverageTimelineStrip coverage={passport.coverage} />
    </div>
  );
}

function CoverageTimelineStrip({
  coverage,
}: {
  coverage: TelemetryPassportClientResponse['coverage'];
}) {
  if (coverage.firstSeen == null || coverage.lastSeen == null) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">No telemetry history observed for this unit yet.</p>
    );
  }

  return (
    <div className="mt-5 border-t border-border pt-5">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Coverage timeline
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        First seen <span className="font-medium text-foreground">{formatDate(coverage.firstSeen)}</span> ·
        {' '}Last seen <span className="font-medium text-foreground">{formatDate(coverage.lastSeen)}</span> ·
        {' '}<span className="font-num">{coverage.observedDays}</span> observed day
        {coverage.observedDays === 1 ? '' : 's'}
      </p>
      {coverage.gaps.length === 0 ? (
        <p className="mt-2 text-sm text-emerald-700">No reporting gaps in this window.</p>
      ) : (
        <div className="mt-3">
          <p className="text-sm text-muted-foreground">
            {coverage.gaps.length} reporting gap{coverage.gaps.length === 1 ? '' : 's'} disclosed
            below — periods with no telemetry_daily_agg rows:
          </p>
          <ul className="mt-2 space-y-1">
            {coverage.gaps.map((gap) => (
              <li
                key={`${gap.start}-${gap.end}`}
                className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
              >
                <span className="font-num">{formatDate(gap.start)}</span>
                <span>&rarr;</span>
                <span className="font-num">{formatDate(gap.end)}</span>
                <span className="text-amber-600">
                  ({gap.days} day{gap.days === 1 ? '' : 's'})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Disclosure, not cryptography: history before onboarding cannot be verified, and silent
        gaps cannot be filled — they are shown, not hidden.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §2 Health & Operational Fitness
// ---------------------------------------------------------------------------

const WEAR_TRAJECTORY_LABEL: Record<'stable' | 'accelerating', string> = {
  stable: 'Stable',
  accelerating: 'Accelerating',
};

function HealthSection({ health }: { health: TelemetryPassportClientResponse['health'] }) {
  if (!health) {
    return <p className="text-sm text-muted-foreground">Telemetry health not yet scored for this unit.</p>;
  }

  const band = horizonBandInfo(health.horizonBand ?? 'critical');

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-num text-3xl font-semibold text-foreground">
          {Math.round(health.overallScore)}
        </span>
        <span className="text-sm text-muted-foreground">/ 100</span>
        {health.horizonBand && <HorizonBandChip band={health.horizonBand} />}
      </div>
      {health.horizonBand && (
        <p className="mt-2 text-sm font-medium text-foreground">{fitnessLine(band)}</p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Scored {formatDate(health.scoredAt)}</p>

      {health.wearTrajectory && (
        <p className="mt-3 text-sm text-muted-foreground">
          Wear trajectory:{' '}
          <span
            className={
              health.wearTrajectory === 'accelerating'
                ? 'font-medium text-red-600'
                : 'font-medium text-emerald-700'
            }
          >
            {WEAR_TRAJECTORY_LABEL[health.wearTrajectory]}
          </span>
        </p>
      )}

      {health.categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {health.categories.map((cat) => (
            <HealthScoreCategoryChip
              key={cat.category}
              category={cat.category}
              score={cat.score}
              coverage={cat.coverage}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §3 Reliability Dossier — damage budget
// ---------------------------------------------------------------------------

function ReliabilityDossier({
  damageBudget,
}: {
  damageBudget: TelemetryPassportClientResponse['damageBudget'];
}) {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Lifetime cumulative counts, measured against this model&rsquo;s own concern thresholds — not
        an invented percentage of factory spares.
      </p>

      {damageBudget.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No reliability metrics available yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {damageBudget.map((metric) => {
            const pct = damageBudgetHeadroomPct(metric.value, metric.criticalThreshold);
            const overThreshold = metric.value >= metric.criticalThreshold;
            return (
              <li key={metric.key}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">
                    {formatDamageBudgetCaption(metric.label, metric.value, metric.criticalThreshold)}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${overThreshold ? 'bg-red-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §4 Operational disclosures
// ---------------------------------------------------------------------------

function OperationalDisclosures({
  disclosures,
}: {
  disclosures: TelemetryPassportClientResponse['disclosures'];
}) {
  return (
    <>
      {disclosures.length === 0 ? (
        <div>
          <p className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700">
            <ShieldCheck className="size-4" /> No disclosures on record
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Checked for: {DISCLOSURES_CHECKED.map((b) => disclosureLabel(b)).join(', ')} — none triggered
            on this unit&rsquo;s lifetime telemetry.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {disclosures.map((disclosure) => (
            <li
              key={disclosure}
              className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="size-4" /> {disclosureLabel(disclosure)}
              </div>
              <p className="mt-1 text-sm text-amber-700">{disclosureExplanation(disclosure)}</p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Active Verification (SIL-585) — always renders: 'none' shows a quiet
// "no run recorded" line, exactly like the pending-classification pattern
// below never silently omits a section.
// ---------------------------------------------------------------------------

/**
 * Exported for the SIL-1349 node Passport drawer, which renders THIS section
 * (and `ProvenanceChainSection` below) without the rest of the §1-§5 stack.
 * Exporting the part is the whole point: a drawer that copied this markup
 * would drift from the passport page it is a summary of, and the two disagreeing
 * about a run is precisely the failure the shared renderer exists to prevent.
 */
export function ActiveVerificationSection({
  verification,
}: {
  verification: TelemetryPassportClientResponse['verification'];
}) {
  return (
    <>
      <p className="text-xs text-muted-foreground">
        A stress test run against this hardware, either through a BMC-attested hardware run or a
        seller-run browser test.
      </p>
      <div className="mt-4">
        <VerificationOutcome verification={verification} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Provenance chain (SIL-821) — the hash-chained ownership/identity history
// (asset_provenance_event, 805), rendered in seq order. A merged asset's
// Passport always resolves to the WINNER's own chain before this ever
// renders (see assemble.ts / 805's migration header) — there is no
// "which chain am I looking at" ambiguity for a reader here.
// ---------------------------------------------------------------------------

/**
 * The current Fingerprint verification tier is shown once, as the frame the
 * whole chain is read through — the same anti-laundering ordering discipline
 * as ConfidenceGauge above, not a per-row tier (a provenance event does not
 * carry its own tier; the tier is a property of the CURRENT verification
 * state, not of history).
 */
/**
 * Exported alongside `ActiveVerificationSection` for the SIL-1349 drawer.
 *
 * `verification` is OPTIONAL there and only there: the drawer's
 * `passport-summary` payload OMITS the Active Verification key entirely when no
 * run has ever completed (SIL-1345 — absent, never null), and an absent run is
 * exactly the `'Unverified'` tier label the fall-through below already
 * produces. Passing a synthesized `{ kind: 'none' }` to satisfy a required prop
 * would be a caller inventing a run state it was not told about.
 */
export function ProvenanceChainSection({
  provenance,
  verification,
}: {
  provenance: TelemetryPassportClientResponse['provenance'];
  verification?: TelemetryPassportClientResponse['verification'];
}) {
  const tierLabel =
    verification?.kind === 'tier12_pass' || verification?.kind === 'tier12_failed' || verification?.kind === 'tier12_flagged'
      ? `Tier ${verification.tier}`
      : verification?.kind === 'tier3_self_reported'
        ? 'Tier 3 (self-reported)'
        : 'Unverified';

  return (
    <>
      <p className="text-xs text-muted-foreground">
        Every event in this Fingerprint&rsquo;s identity history, in order. Each row is chained to the
        one before it — an alteration to any row after the fact breaks every row after it. Current
        verification tier: <span className="font-medium text-foreground">{tierLabel}</span>.
      </p>

      {provenance.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No provenance events recorded yet.</p>
      ) : (
        <ol className="mt-4 space-y-3 border-l border-border pl-4">
          {provenance.map((event) => (
            <li key={event.id} className="relative">
              <span className="absolute -left-[1.1rem] top-1.5 size-2 rounded-full bg-border" />
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium text-foreground">{provenanceEventDescription(event)}</span>
                <span className="text-xs text-muted-foreground">{formatDate(event.occurredAt)}</span>
              </div>
              {event.manufacturerSerial && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Serial: {maskSerial(event.manufacturerSerial)}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Asset lifecycle — the same dated history the issued PDF prints, from the
// same helper (passport/display.ts). It sits inside the provenance panel
// rather than in a panel of its own: the chain answers "who held it and what
// changed", the lifecycle answers "when", and a reader following custody
// should not have to open a second section to get the dates.
//
// States recorded facts only. No remaining-life number and no end-of-life
// prediction — the scoring model is a fitness band, not a probabilistic RUL
// (scoring/horizon-band.ts), the same restraint PassportDocument observes.
// ---------------------------------------------------------------------------

function LifecycleSection({ passport }: { passport: TelemetryPassportClientResponse }) {
  const milestones = lifecycleMilestones(passport);

  return (
    <div className="mt-6 border-t border-border pt-4">
      <h3 className="text-sm font-medium text-foreground">Lifecycle</h3>
      {milestones.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No dated lifecycle events on record for this asset yet.
        </p>
      ) : (
        <dl className="mt-3 space-y-1.5" data-testid="passport-lifecycle">
          {milestones.map((milestone, index) => (
            <div key={`${milestone.date}-${index}`} className="flex flex-wrap items-baseline gap-x-3">
              <dt className="text-xs tabular-nums text-muted-foreground">{formatDate(milestone.date)}</dt>
              <dd className="text-sm text-foreground">{milestone.label}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        This lifecycle is a record of observed and recorded events. It is not a prediction of
        remaining service life, and it is not a warranty.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component manifest (SIL-825) — the current component set + dated,
// tier-qualified change history (asset_manifest_version/
// asset_manifest_component, 811). Every entry below is rendered as a FACT
// ("GPU in slot 3 changed on this date, confirmed out-of-band tier N"),
// never a verdict, a warning, or a risk score — no colour, icon, or copy in
// this section may imply severity. The buyer judges what a change means;
// this states only that it happened, when, and at what verification tier.
// ---------------------------------------------------------------------------

const MANIFEST_DEVICE_LABEL: Record<string, string> = {
  chassis: 'Chassis',
  gpu: 'GPU',
  accelerator: 'Accelerator',
};

function manifestSlotLabel(deviceKind: string, deviceIndex: number | null): string {
  const kind = MANIFEST_DEVICE_LABEL[deviceKind] ?? deviceKind;
  return deviceIndex == null ? kind : `${kind} slot ${deviceIndex}`;
}

function ManifestSection({ manifest }: { manifest: TelemetryPassportClientResponse['manifest'] }) {
  const { current, history } = manifest;

  if (!current) {
    return <p className="text-sm text-muted-foreground">No component manifest recorded yet.</p>;
  }

  return (
    <>
      <p className="text-xs text-muted-foreground">
        Recorded {formatDate(current.recordedAt)}, Tier {current.verificationTier} verification (version{' '}
        {current.versionSeq}). A manifest is a dated claim, not a permanent property — hardware moves, and a
        component swap shown below is a fact for the buyer to judge, not a finding against the seller.
      </p>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="pb-1 pr-3 font-medium">Slot</th>
            <th className="pb-1 pr-3 font-medium">Manufacturer</th>
            <th className="pb-1 pr-3 font-medium">Model</th>
            <th className="pb-1 font-medium">Serial</th>
          </tr>
        </thead>
        <tbody>
          {current.components.map((c, i) => (
            <tr key={`${c.deviceKind}-${c.deviceIndex ?? i}`} className="border-b border-border/50 last:border-0">
              <td className="py-1 pr-3 text-foreground">{manifestSlotLabel(c.deviceKind, c.deviceIndex)}</td>
              <td className="py-1 pr-3 text-muted-foreground">{c.manufacturerName ?? '—'}</td>
              <td className="py-1 pr-3 text-muted-foreground">{c.modelName ?? '—'}</td>
              <td className="py-1 text-muted-foreground">{c.normalizedSerial ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {history.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Change history
          </h3>
          <ol className="mt-3 space-y-3 border-l border-border pl-4">
            {history.map((entry, entryIdx) => (
              <li key={entryIdx} className="relative">
                <span className="absolute -left-[1.1rem] top-1.5 size-2 rounded-full bg-border" />
                {entry.changes.map((change, i) => (
                  <div key={i} className="mb-2 last:mb-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium text-foreground">
                        {manifestSlotLabel(change.deviceKind, change.deviceIndex)} changed
                      </span>
                      <span className="text-xs text-muted-foreground">
                        between {formatDate(entry.fromRecordedAt)} (Tier {entry.fromVerificationTier}) and{' '}
                        {formatDate(entry.toRecordedAt)} (Tier {entry.toVerificationTier})
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Was: {change.from ? `${change.from.manufacturerName ?? 'Unknown'} · ${change.from.normalizedSerial ?? '—'}` : 'not present'}
                      {' → '}
                      Now: {change.to ? `${change.to.manufacturerName ?? 'Unknown'} · ${change.to.normalizedSerial ?? '—'}` : 'removed'}
                    </p>
                  </div>
                ))}
              </li>
            ))}
          </ol>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Environmental — embodied carbon (SIL-190) + operational Scope 2. Always
// renders: a computed snapshot shows value + bounds + methodology +
// disclaimer; no SKU / no snapshot shows the pending-classification
// placeholder (locked decision — never silently omitted).
//
// The two figures are deliberately kept visually separate and never summed:
// embodied is a one-time total for the hardware, Scope 2 is an annual rate at
// the asset's current site. The disclaimer says so in words; the layout says
// so in shape.
// ---------------------------------------------------------------------------

export const ENVIRONMENTAL_SECTION_TITLE = 'Environmental — carbon';

function fmtKg(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * The environmental content, without a card wrapper — what `PassportBody`
 * drops inside its `<Panel>`.
 *
 * `environmental.scope2` is rendered only when present. Null means the payload
 * predates Scope 2 reporting (a pre-v4 artifact), and that document made no
 * Scope 2 claim, so showing a placeholder there would invent one. A figure with
 * `status: 'not_computed'` is the opposite case — we looked and had nothing —
 * and gets an explicit line naming the reason.
 */
function EnvironmentalBody({
  environmental,
}: {
  environmental: TelemetryPassportClientResponse['environmental'];
}) {
  const fp = environmental.carbonFootprint;
  const scope2 = environmental.scope2;
  return (
    <>
      <p className="text-xs text-muted-foreground">{CARBON_EMBODIED_EXPLAINER}</p>

      {fp == null ? (
        <p className="mt-4 text-sm text-muted-foreground">{PASSPORT_ENVIRONMENTAL_PENDING_TEXT}</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-baseline gap-2">
            <span className="inline-flex items-center gap-1.5">
              <Leaf className="size-4 text-emerald-600" />
              <span className="font-num text-3xl font-semibold text-foreground">
                {fmtKg(fp.valueKgco2eq)}
              </span>
            </span>
            <span className="text-sm text-muted-foreground">
              kgCO&#8322;eq &middot; range {fmtKg(fp.minKgco2eq)}&ndash;{fmtKg(fp.maxKgco2eq)}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {fp.completeness === 'complete' ? (
              <span className="font-medium text-emerald-700">
                Complete &mdash; no defaulted inputs
              </span>
            ) : (
              <span className="font-medium text-amber-700">
                Partial &mdash; some inputs use industry archetype defaults
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Methodology: Boavizta open-source
          </p>
        </>
      )}

      {scope2 && <Scope2Block scope2={scope2} />}

      <p className="mt-3 text-xs text-muted-foreground">{environmental.disclaimer}</p>
      {environmental.regulatoryLine && (
        <p className="mt-2 text-xs text-muted-foreground">{environmental.regulatoryLine}</p>
      )}
    </>
  );
}

/**
 * Operational (Scope 2, location-based) carbon.
 *
 * Every string that qualifies the number comes from the response, never from
 * this component: `scopeLabel`, `noMarketBasedStatement`, `boundsCaveat` and
 * `screeningEstimateLine` are literal-typed in dto/use-phase.ts precisely so a
 * renderer cannot show the figure while quietly dropping its qualifiers. Do not
 * substitute hand-written copy for any of them.
 *
 * The "/yr" is part of the headline, not a footnote — this is a rate, and a
 * reader who mistakes it for a lifetime total will understate by the asset's
 * whole service life.
 */
function Scope2Block({
  scope2,
}: {
  scope2: NonNullable<TelemetryPassportClientResponse['environmental']['scope2']>;
}) {
  const { figure } = scope2;
  return (
    <div className="mt-5 border-t border-border pt-5">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {scope2.scopeLabel}
      </div>

      {figure.status === 'not_computed' ? (
        <p className="mt-2 text-sm text-muted-foreground">{usePhaseNotComputedLine(figure.reason)}</p>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-baseline gap-2">
            <span className="inline-flex items-center gap-1.5">
              <Plug className="size-4 text-sky-600" />
              <span className="font-num text-3xl font-semibold text-foreground">
                {fmtKg(figure.gwpUseAnnualKgco2eq)}
              </span>
            </span>
            <span className="text-sm text-muted-foreground">
              kgCO&#8322;e/yr &middot; range {fmtKg(figure.gwpUseAnnualMinKgco2eq)}&ndash;
              {fmtKg(figure.gwpUseAnnualMaxKgco2eq)}
            </span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Grid intensity for {figure.usageLocation}, at a data-centre PUE of {figure.pue}. An
            annual rate at this asset&rsquo;s current site &mdash; it does not transfer with the
            asset.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">{scope2.screeningEstimateLine}</p>
          <p className="mt-2 text-xs text-muted-foreground">{scope2.boundsCaveat}</p>
          {figure.acceleratorAssumption && (
            <p className="mt-2 text-xs text-muted-foreground">{figure.acceleratorAssumption}</p>
          )}
        </>
      )}
      <p className="mt-2 text-xs text-muted-foreground">{scope2.noMarketBasedStatement}</p>
    </div>
  );
}

/**
 * Exported for the asset-detail surfaces (v1 card style matches theirs); the
 * passport stack and asset detail render the identical embodied figure.
 *
 * EMBODIED ONLY, deliberately. Asset detail already renders operational carbon
 * in its own `UsePhaseCard`, so this card drops `scope2` on the way in rather
 * than showing the same Scope 2 number twice on one page. Hence the narrower
 * heading too — this card really does only report embodied carbon, and the
 * combined "Environmental — carbon" title belongs to the passport panel, which
 * carries both.
 */
export function EnvironmentalSection({
  environmental,
}: {
  environmental: TelemetryPassportClientResponse['environmental'];
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold text-foreground">
        Environmental &mdash; embodied carbon
      </h2>
      <div className="mt-1">
        <EnvironmentalBody environmental={{ ...environmental, scope2: null }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §5 Disclaimer & Verification (FMV line only renders when the API includes
// passport.fmv — nulled for non-owner client callers; always present for ops)
// ---------------------------------------------------------------------------

function DisclaimerSection({ passport }: { passport: TelemetryPassportClientResponse }) {
  return (
    <>
      <p className="text-sm text-muted-foreground">{passport.disclaimer}</p>
      {passport.fmv && (
        <p className="mt-3 text-sm text-muted-foreground">
          Latest fair market value:{' '}
          <UsdCell value={passport.fmv.medianUsd} className="font-medium text-foreground" /> as of{' '}
          {formatDate(passport.fmv.pricedAt)}.
          {passport.fmv.isDirectionalBaseline && (
            <span className="ml-1 font-medium text-amber-700">({DIRECTIONAL_BASELINE_LABEL})</span>
          )}
        </p>
      )}
    </>
  );
}
